import { Course } from "@/models/courses.model";
import { QueryParams } from "@/models/query-params.model";
import { Roles, Status } from "@/models/user.model";
import { CoursesRest } from "@/services/rest/courses.rest";
import { OrganizationsRest } from "@/services/rest/organizations.rest";
import { UsersRest } from "@/services/rest/users.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { BehaviorSubject, map, take } from "rxjs";

interface Item {
  id: string | number;
}

export class UsersService {
  constructor(
    private _users = new UsersRest(),
    private _orgs = new OrganizationsRest(),
    private _courses = new CoursesRest(),
    private go = new GoToRoute()
  ) {}

  private users$ = new BehaviorSubject<any>([]);
  users = this.users$.asObservable();

  private user$ = new BehaviorSubject<any>([]);
  user = this.user$.asObservable();

  private useStudent$ = new BehaviorSubject<any>([]);
  useStudent = this.useStudent$.asObservable();

  private orgs$ = new BehaviorSubject<any>([]);
  orgs = this.orgs$.asObservable();

  private courses$ = new BehaviorSubject<any>([]);
  courses = this.courses$.asObservable();

  role: string = "";

  getUsers(queryParams: QueryParams): void {
    this._users
      .getUsers(queryParams)
      .pipe(
        take(1),
        map((e: any) => {
          e.content.forEach((item: any) => {
            item.status = this.remakeObjectStatus(item.status);
            item.role = this.remakeObjectRole(item.role);
          });
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.users$.next(response);
        },
      });
  }

  getUser(userId: string): void {
    this._users
      .getUserById(userId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.role = response.role;
          this.user$.next(response);
        },
        error: () => {
          this.go.redirectToPage("users");
        },
      });
  }

  getUserStudent(userId: string): void {
    this._users
      .getUserStudentById(userId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.useStudent$.next(response);
        },
        error: () => {
          this.go.redirectToPage("users");
        },
      });
  }

  getOrgs(queryParams: QueryParams): void {
    this._orgs.getOrganizations(queryParams).subscribe({
      next: (response) => {
        this.orgs$.next(response.content);
      },
    });
  }

  getCourses(queryParams: QueryParams): void {
    this._courses
      .getCourses(queryParams)
      .pipe(take(1))
      .subscribe((response) => {
        this.courses$.next(response);
      });
  }

  getCoursesAvailable(): void {
    this._courses
      .getCoursesAdminAvailable()
      .pipe(take(1))
      .subscribe((response) => {
        this.courses$.next(response);
      });
  }

  addCourseField(items: Course[]): void {
    items.push(new Course());
  }

  removeCourseField(items: Course[], index: number): void {
    items?.splice(index, 1);
  }

  removeCourse(courses: Course[], index: number): void {
    courses.splice(index, 1);
  }

  isAlreadyExists(obj: Item, list: Item[]): boolean {
    return list?.some((item) => item?.id === obj?.id) || false;
  }

  getRoles(role: string): string {
    if (role == Roles.ADMINISTRATOR) {
      role = "Administrador";
    } else if (role == Roles.TEACHER) {
      role = "Professor";
    } else if (role == Roles.STUDENT) {
      role = "Aluno";
    }
    return role;
  }

  getStatus(status: string): string {
    if (status == Status.ACTIVATED) {
      status = "Ativo";
    } else if (status == Status.INACTIVATED) {
      status = "Inativo";
    } else if (status == Status.BLOCKED) {
      status = "Bloqueado";
    } else if (status == Status.WAITING_ACTIVATION) {
      status = "Aguardando";
    }
    return status;
  }

  getStatusColor(status: string): string {
    if (status == Status.ACTIVATED) {
      status = "bg-h-success-100 text-h-success-500";
    } else if (status == Status.INACTIVATED) {
      status = "bg-h-neutral-100 text-h-neutral-800";
    } else if (status == Status.BLOCKED) {
      status = "bg-h-error-100 text-h-error-800";
    } else if (status == Status.WAITING_ACTIVATION) {
      status = "bg-h-warning-100 text-h-warning-800";
    }
    return status;
  }

  csvToArray(str: any, delimiter = ","): any[] {
    const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
    const rows = str.slice(str.indexOf("\n") + 1).split("\n");
    const arr = rows.map((row: any) => {
      const values = row.split(delimiter);
      const el = headers.reduce((object: any, header: any, index: any) => {
        object[header.replace('"', "").replace('"', "")] = values[index];
        return object;
      }, {});
      return el;
    });
    return arr;
  }

  errorHandler(code: any): string {
    return Messages[code?.replace('"', "")?.replace('"', "")];
  }

  remakeObjectStatus(item: any): any {
    var status = { name: "", class: "", value: "" };
    status.name = this.getStatus(item);
    status.class = this.getStatusColor(item);
    status.value = item;
    return status;
  }

  remakeObjectRole(item: any): any {
    var role = { name: "", value: "" };
    role.name = this.getRoles(item);
    role.value = item;
    return role;
  }

  statusChange(status: string): string {
    switch (status) {
      case Status.ACTIVATED:
        return Status.BLOCKED;
      case Status.BLOCKED:
        return Status.ACTIVATED;
    }
    return status;
  }
}
